<template>
  <div>
    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <v-card-title>Repasse</v-card-title>
        <v-card-text>
          <v-card outlined class="mb-4">
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th class="text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Receita bruta de vendas</td>
                    <td
                      :class="
                        reducedPayments.totalAmount > 0 ? 'success--text' : ''
                      "
                      class="text-right"
                    >
                      {{ reducedPayments.totalAmount | currency }}
                    </td>
                  </tr>
                  <tr>
                    <td>Taxa da plataforma</td>
                    <td
                      :class="
                        reducedPayments.totalPlatformFee > 0
                          ? 'error--text'
                          : ''
                      "
                      class="text-right"
                    >
                      {{ -reducedPayments.totalPlatformFee | currency }}
                    </td>
                  </tr>
                  <tr>
                    <td>Solicitações de recursos</td>
                    <td
                      :class="
                        reducedResourceRequests.totalAmount > 0
                          ? 'error--text'
                          : ''
                      "
                      class="text-right"
                    >
                      {{ -reducedResourceRequests.totalAmount | currency }}
                    </td>
                  </tr>
                  <tr>
                    <td>Ajustes</td>
                    <td
                      :class="
                        reducedAdjustments < 0
                          ? 'error--text'
                          : reducedAdjustments > 0
                          ? 'success--text'
                          : ''
                      "
                      class="text-right"
                    >
                      {{ reducedAdjustments | currency }}
                    </td>
                  </tr>
                  <tr v-if="reducedWithdrawals.pending > 0">
                    <td>Repasses pendentes</td>
                    <td class="text-right error--text">
                      {{ -reducedWithdrawals.pending | currency }}
                    </td>
                  </tr>
                  <tr>
                    <td>Repasses pagos</td>
                    <td class="text-right error--text">
                      {{ -reducedWithdrawals.paid | currency }}
                    </td>
                  </tr>
                </tbody>
                <td colspan="2">
                  <v-divider />
                  <v-divider />
                </td>
                <tfoot>
                  <tr class="font-weight-bold">
                    <td>Saldo</td>
                    <td
                      :class="total > 0 ? 'success--text' : 'error--text'"
                      class="text-right"
                    >
                      <v-btn v-if="total > 0" @click="selectTotal" x-small text>
                        Repassar total
                      </v-btn>
                      {{ total | currency }}
                    </td>
                  </tr>
                </tfoot>
              </template>
            </v-simple-table>
          </v-card>

          <v-form v-model="isValid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="withdrawal.amount"
                  autofocus
                  label="Valor"
                  type="number"
                  :prefix="`R$`"
                  required
                  outlined
                  hide-details
                  min="0"
                  :rules="[
                    (v) => !!v || 'Valor é obrigatório',
                    (v) => v > 0 || 'Valor deve ser maior que 0',
                  ]"
                />

                <v-alert v-if="total < 0" class="mt-3 mb-0" type="error" text>
                  O saldo é negativo, portanto, qualquer repasse será
                  considerado como adiantamento.
                </v-alert>
                <v-alert
                  v-else-if="withdrawal.amount > total"
                  class="mt-3 mb-0"
                  type="error"
                  text
                >
                  O valor do repasse é
                  {{ (withdrawal.amount - total) | currency(true) }} maior que o
                  saldo. Portanto, o repasse será considerado como um
                  adiantamento.
                </v-alert>
              </v-col>
              <v-col cols="12">
                <OrganizationBankAccountSelector
                  v-model="withdrawal.bankAccount"
                  :organization="party.Organization"
                  :rules="[(v) => !!v || 'Conta bancária é obrigatória']"
                />

                <v-btn
                  @click="openPixQRCodeDialog"
                  small
                  block
                  text
                  color="primary"
                  :disabled="!withdrawal.bankAccount || !withdrawal.amount"
                  class="mt-3"
                >
                  Gerar Código PIX
                </v-btn>
              </v-col>
              <v-col cols="12" class="pb-0">
                <v-textarea
                  v-model="withdrawal.observation"
                  label="Observação"
                  outlined
                  rows="3"
                  counter
                  :maxlength="190"
                  :rules="[
                    (v) =>
                      !v ||
                      v.length <= 190 ||
                      'Observação deve ter menos de 190 caracteres',
                  ]"
                />
              </v-col>
              <v-col cols="12">
                <v-btn-toggle
                  v-model="withdrawal.status"
                  mandatory
                  class="d-flex"
                  :color="withdrawal.status === 'PAID' ? 'success' : 'warning'"
                >
                  <v-btn value="PAID" class="flex-grow-1" small>Pago</v-btn>
                  <v-btn value="PENDING" class="flex-grow-1" small>
                    Pendente
                  </v-btn>
                </v-btn-toggle>
              </v-col>
              <v-col cols="12" v-if="withdrawal.status === 'PAID'">
                <v-file-input
                  v-model="withdrawal.proofDocument"
                  label="Comprovante"
                  outlined
                  hide-details
                  accept="image/png, image/jpeg, application/pdf"
                />
              </v-col>
              <v-col cols="12" v-if="withdrawal.status === 'PENDING'">
                <v-alert type="info" text class="mb-0">
                  O repasse ficará pendente até que o pagamento seja confirmado.
                </v-alert>
              </v-col>
            </v-row>
          </v-form>
          <v-alert v-if="error" type="error" text class="mt-3 mb-0">
            {{ error }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="close" text :disabled="loading">Cancelar</v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            @click="save"
            :loading="loading"
            :disabled="!isValid"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="pixQRCodeDialog"
      max-width="300"
      content-class="rounded-xl"
    >
      <v-card>
        <div class="text-center pa-2">
          <h5>Pix Copia e Cola</h5>
        </div>
        <v-card-text class="d-flex flex-column align-center">
          <QrcodeVue :value="pixQRCode" :size="256" />
          <v-alert
            v-if="withdrawal.bankAccount"
            color="info"
            text
            dense
            class="w-full mt-3 mb-0"
          >
            <span class="d-block">
              Valor: <b>{{ withdrawal.amount | currency }}</b>
            </span>
            <span class="d-block">
              Banco: <b>{{ withdrawal.bankAccount.bank }}</b>
            </span>
            <span class="d-block">
              Beneficiário: <b>{{ withdrawal.bankAccount.beneficiary }}</b>
            </span>
            <span class="d-block">
              Chave: <b>{{ withdrawal.bankAccount.pixKey }}</b>
            </span>
          </v-alert>

          <v-text-field
            :value="pixQRCode"
            label="Copia e cola"
            readonly
            outlined
            dense
            class="mt-3 w-full"
            hide-details
          />

          <v-btn
            @click="copyToClipboard(pixQRCode)"
            small
            text
            color="primary"
            :disabled="successCopied"
            class="mt-3"
          >
            {{ successCopied ? "Copiado" : "Copiar" }}
            <v-icon v-if="successCopied" class="ml-2" x-small>mdi-check</v-icon>
            <v-icon v-else class="ml-2" x-small>mdi-content-copy</v-icon>
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <ConfirmModal ref="confirmModal" />
  </div>
</template>

<script>
import PARTY from "@/services/admin/parties";
import ConfirmModal from "@/components/global/ConfirmModal";
import OrganizationBankAccountSelector from "../../../global/selector/OrganizationBankAccountSelector.vue";
import QrcodeVue from "qrcode.vue";
import { generatePixQRCode } from "@/utils/pix-generator";
export default {
  components: {
    ConfirmModal,
    OrganizationBankAccountSelector,
    QrcodeVue,
  },
  props: {
    party: {
      type: Object,
      required: true,
    },
    reducedPayments: {
      type: Object,
      required: true,
    },
    reducedResourceRequests: {
      type: Object,
      required: true,
    },
    reducedAdjustments: {
      type: Number,
      required: true,
    },
    reducedWithdrawals: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      dialog: false,
      isValid: false,
      loading: false,
      error: null,
      pixQRCode: null,
      pixQRCodeDialog: false,
      successCopied: false,
      withdrawal: this.defaultWithdrawal(),
      statusList: [
        {
          value: "PAID",
          text: "Pago",
        },
        {
          value: "PENDING",
          text: "Pendente",
        },
      ],
    };
  },
  methods: {
    open(withdrawal) {
      this.withdrawal = Object.assign({}, this.defaultWithdrawal(), withdrawal);
      this.dialog = true;
    },
    close() {
      this.dialog = false;
    },
    async save() {
      this.loading = true;
      this.error = null;
      try {
        const data = {
          amount: this.withdrawal.amount,
          bankAccountId: this.withdrawal.bankAccount.id,
          type: this.withdrawal.type,
          observation: this.withdrawal.observation,
          status: this.withdrawal.status,
        };

        if (
          this.withdrawal.status === "PAID" &&
          !this.withdrawal.proofDocument
        ) {
          const confirm = await this.$refs.confirmModal.confirm({
            icon: "mdi-alert",
            title: "Atenção",
            message:
              "O comprovante é opcional, mas é altamente recomendado que seja anexado ao repasse. <br><br> Tem certeza que deseja continuar <b class='font-weight-bold'>sem anexar o comprovante</b>?",
            buttons: {
              cancel: {
                label: "Cancelar",
                color: "primary",
              },
              confirm: {
                label: "Confirmar",
                color: "error",
              },
            },
          });
          if (!confirm) return;
        }

        const { withdrawal } = await PARTY.createWithdrawal(
          this.party.id,
          data
        );
        console.log(withdrawal);
        if (this.withdrawal.proofDocument) {
          await PARTY.uploadWithdrawalProofDocument(
            this.party.id,
            withdrawal.id,
            this.withdrawal.proofDocument
          );
        }

        this.$emit("success");
        this.close();
      } catch (error) {
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },
    openPixQRCodeDialog() {
      this.pixQRCode = generatePixQRCode({
        amount: parseFloat(this.withdrawal.amount),
        pixKey: this.withdrawal.bankAccount.pixKey,
        pixKeyType: this.withdrawal.bankAccount.pixKeyType,
        name: (

          this.withdrawal.bankAccount.beneficiary ||
          this.party.Organization?.name ||
          "N"
        ).substring(0, 14),
        city: this.party.Organization?.city || "C",
      });
      this.pixQRCodeDialog = true;
    },
    copyToClipboard(value) {
      navigator.clipboard.writeText(value);
      this.successCopied = true;
      setTimeout(() => {
        this.successCopied = false;
      }, 2000);
    },
    defaultWithdrawal() {
      return {
        amount: null,
        observation: "",
        proofDocument: null,
        status: "PAID",
        bankAccount: null,
      };
    },
    selectTotal() {
      const total = parseFloat(this.total.toFixed(2));
      this.withdrawal.amount = total;
    },
  },
  computed: {
    total() {
      const total =
        this.reducedPayments.totalAmount -
        this.reducedPayments.totalPlatformFee -
        this.reducedResourceRequests.totalPartyTotalDebit +
        this.reducedAdjustments -
        this.reducedWithdrawals.pending -
        this.reducedWithdrawals.paid;

      return parseFloat(total.toFixed(2));
    },
  },
  mounted() {
    this.$root.$on("party-withdrawals-modal", this.open);
  },
};
</script>
